import { useDocumentReferences } from '@ctw/shared/api/fhir/document';
import type { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  type Evidence,
  isRiskGapUserStatus,
  type NewGapType,
  type RiskGapModel,
  type RiskGapType,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { notify } from '@ctw/shared/components/toast';
import {
  ClinicalObjectGroup,
  type ClinicalObjectGroupItem,
} from '@ctw/shared/content/clinical-object/clinical-object-group';
import { RiskGapDetailsCard } from '@ctw/shared/content/risk/risk-gap-details-card';
import { RiskGapDisclaimer } from '@ctw/shared/content/risk/risk-gap-disclaimer';
import { RiskGapFeedbackCard } from '@ctw/shared/content/risk/risk-gap-feedback-card';
import { riskGapStatusBadgeDefinition } from '@ctw/shared/content/risk/risk-gap-status-badge-definition';
import { RiskGapStatusForm } from '@ctw/shared/content/risk/risk-gap-status-form';
import {
  fetchResourceByTypeAndIds,
  useFqsHotstorageRequestOptions,
  useResources,
} from '@ctw/shared/context/_internal/resource/resource-fetch';
import type { CtwFetchFn } from '@ctw/shared/context/ctw-context';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContextIfAvailable } from '@ctw/shared/context/patient-provider';
import {
  type OpenResourceDrawerAction,
  type UseResourceDrawerActionArgs,
  useResourceDrawer,
} from '@ctw/shared/context/resource-drawer-context';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import { TelemetryBoundary, useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { getStandaloneBaseUrl } from '@ctw/shared/utils/environments';
import { tw } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { useQueryClient } from '@tanstack/react-query';
import { SYSTEM_URLS } from '@zeus-health/types';
import type { Condition, DiagnosticReport, DocumentReference, Resource } from 'fhir/r4';
import { capitalize, compact, snakeCase, startCase } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

/**
 * MeasureReport drawers come in two variants:
 *
 * 1. Risk Gap drawer – full workflow: details card, status form, clinical evidence,
 *    related records, feedback, disclaimer. Used for standard risk gap MeasureReports.
 *    Note: This is deprecated and will be removed in a future release.
 *
 * 2. Payer Risk Gap drawer – simplified read-only view: details (HCC, ICD-10, status,
 *    recapture, period). Used for MeasureReports with the Payer Risk Gap profile.
 */

const IMAGE_RE = /^image\//i;
const PDF_RE = /^application\/.*(pdf|fdf)$/i;

/** True when the MeasureReport uses the Payer Risk Gap profile (simplified drawer). */
function isPayerRiskGap(model: RiskGapModel): boolean {
  return (
    model.resource.meta?.profile?.includes(SYSTEM_URLS.PROFILE.PAYER_RISK_GAP_MEASURE_REPORT) ??
    false
  );
}

/** Returns the Payer Risk Gap status from the MeasureReport extension, if present. */
function getPayerRiskGapStatus(model: RiskGapModel): string | undefined {
  return model.resource.extension?.find(
    (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.PAYER_RISK_GAP.STATUS,
  )?.valueString;
}

/** Returns the recapture flag (Y/N) from the Payer Risk Gap extension, if present. */
function getPayerRiskGapRecaptureFlag(model: RiskGapModel): string | undefined {
  const value = model.resource.extension?.find(
    (ext) => ext.url === SYSTEM_URLS.EXTENSIONS.PAYER_RISK_GAP.RECAPTURE_FLAG,
  )?.valueString;
  if (value === '1') {
    return 'Y';
  }
  if (value === '0') {
    return 'N';
  }
  return undefined;
}

/** Builds the Payer Risk Gap drawer action (simplified read-only drawer). */
function payerRiskGapDrawerAction(model: RiskGapModel): OpenResourceDrawerAction {
  const payerStatus = getPayerRiskGapStatus(model);
  const recaptureFlag = getPayerRiskGapRecaptureFlag(model);
  const periodStart = model.resource.period?.start;
  const periodEnd = model.resource.period?.end;
  let periodDisplay = model.period;
  if (periodStart && periodEnd) {
    periodDisplay = `${formatFHIRDate(periodStart)} – ${formatFHIRDate(periodEnd)}`;
  }

  return {
    label: 'Payer Risk Gap',
    title: model.title || 'Payer Risk Gap',
    content: {
      body: (
        <div className={tw`space-y-2`}>
          <ContentCard variant="focal" title="Details">
            <DefinitionList
              className={tw`pt-2 pb-0`}
              items={compact([
                { label: 'HCC', value: `${model.hccCode}` },
                model.icd10Codes.length > 0
                  ? {
                      label: 'ICD-10',
                      value: (
                        <ul className={tw`flex flex-col gap-1`}>
                          {model.icd10Codes.map((code) => (
                            <li key={code.code}>
                              <span className={tw`font-medium`}>{code.code}</span>
                              {code.display && <span>&nbsp;{code.display}</span>}
                            </li>
                          ))}
                        </ul>
                      ),
                    }
                  : null,
                payerStatus ? { label: 'Status', value: capitalize(payerStatus) } : null,
                recaptureFlag !== undefined ? { label: 'Recapture', value: recaptureFlag } : null,
                periodDisplay ? { label: 'Period', value: periodDisplay } : null,
              ])}
            />
          </ContentCard>
        </div>
      ),
    },
  };
}

/** Builds the Risk Gap drawer action (full workflow: details, status form, evidence, related records, feedback). */
function riskGapDrawerAction(
  args: UseResourceDrawerActionArgs<'MeasureReport', RiskGapModel>,
): OpenResourceDrawerAction {
  const { model, patient, ctwFetch, requestContext, queryClient, reloadResource } = args;
  return {
    label: riskGapTypeToTitle(model.type),
    title: model.title,
    badges: [riskGapStatusBadgeDefinition(model)],
    content: {
      body: (
        <TelemetryBoundary
          boundaryName="MeasureReportDrawerAction"
          initialContext={{
            riskGapStatus: model.status,
            riskGapSubstatuses: model.substatuses,
            riskGapType: model.type,
            riskGapSource: model.gapSource,
            riskGapConfidenceLevel: model.confidenceLevel,
          }}
        >
          {({ telemetry }) => (
            <div className={tw`space-y-2`}>
              <RiskGapDetailsCard riskGap={model} />
              {isRiskGapUserStatus(model.status) && (
                <RiskGapStatusForm
                  initialStatus={model.status}
                  initialSubstatuses={model.substatuses}
                  initialNotes={model.lastStatusUpdate?.note ?? ''}
                  onSubmit={(data) => {
                    return new Promise((resolve, reject) => {
                      void ctwFetch(
                        `${getStandaloneBaseUrl(requestContext.env)}/api/risk-gaps/${model.id}/status-updates`,
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${requestContext.authToken}`,
                          },
                          body: JSON.stringify({
                            data: {
                              type: 'risk-gap-status-update',
                              attributes: {
                                status: data.status,
                                substatuses: data.substatuses,
                                note: data.notes,
                              },
                            },
                          }),
                        },
                      )
                        .then((fetchResponse: Awaited<ReturnType<CtwFetchFn>>) => {
                          if (fetchResponse.ok) {
                            notify({
                              type: 'success',
                              title: 'The status has been updated.',
                            });
                          } else {
                            telemetry.trackError({
                              error: new Error('Error updating Risk Gap status'),
                              context: {
                                data: JSON.stringify(fetchResponse.data),
                                headers: JSON.stringify(fetchResponse.headers),
                              },
                            });
                            notify({
                              type: 'error',
                              title: 'Unable to update the risk gap status.',
                              body: 'Please try again later or contact support if the issue persists.',
                            });
                          }
                          void queryClient.invalidateQueries();
                          void reloadResource();
                          resolve();
                        })
                        .catch((error) => {
                          telemetry.trackError({
                            error,
                          });
                          notify({
                            type: 'error',
                            title: 'Unable to update the risk gap status.',
                            body: 'Please try again later or contact support if the issue persists.',
                          });
                          reject();
                        });
                    });
                  }}
                />
              )}
              <MeasureReportClinicalEvidence riskGap={model} patient={patient} />
              <MeasureReportRelatedRecords riskGap={model} patient={patient} />
              <RiskGapFeedbackCard riskGap={model} />
              <div className={tw`flex flex-col gap-4 px-4`}>
                <RiskGapDisclaimer />
              </div>
            </div>
          )}
        </TelemetryBoundary>
      ),
    },
  };
}

export const measureReportDrawerAction: (
  args: UseResourceDrawerActionArgs<'MeasureReport', RiskGapModel>,
) => OpenResourceDrawerAction = (args) => {
  if (isPayerRiskGap(args.model)) {
    return payerRiskGapDrawerAction(args.model);
  }
  return riskGapDrawerAction(args);
};

interface MeasureReportClinicalEvidenceProps {
  riskGap: RiskGapModel;
  patient: PatientModel;
}

const MeasureReportClinicalEvidence = ({
  riskGap,
  patient,
}: MeasureReportClinicalEvidenceProps) => {
  const telemetry = useTelemetry();
  const { requestContext, ctwFetch, graphqlClient } = useCTW();
  const queryClient = useQueryClient();
  const { openResourceModal } = useResourceModal();
  const { pushView } = useResourceDrawer();
  const fqsHotstorageRequestOptions = useFqsHotstorageRequestOptions();
  const [loading, setLoading] = useState(true);
  const [evidenceItems, setEvidenceItems] = useState<Array<ClinicalObjectGroupItem>>([]);

  const patientContext = usePatientContextIfAvailable();
  // biome-ignore lint/correctness/useExhaustiveDependencies: Key off `riskGap.id` instead of `riskGap.evidence` to avoid unnecessary re-renders
  const documentIds = useMemo(
    () =>
      riskGap.evidence
        .map((evidence) =>
          evidence.resourceType === 'DocumentReference' ? evidence.resourceId : null,
        )
        .filter(Boolean) as Array<string>,
    [riskGap.id],
  );

  const documentReferencesQuery = useDocumentReferences(patient.UPID ?? '', documentIds);

  type ClinicalEvidenceResource = {
    evidence: Evidence;
    resource: FHIRModel<Resource> | null;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Key off `riskGap.id` instead of `riskGap.evidence` to avoid unnecessary re-renders
  useEffect(() => {
    if (documentIds.length === 0 || Array.isArray(documentReferencesQuery.data)) {
      const evidenceResourcePromises: Array<Promise<ClinicalEvidenceResource>> =
        riskGap.evidence.map((evidence) => {
          if (!(evidence.resourceType && evidence.resourceId)) {
            return Promise.resolve({ evidence, resource: null });
          }
          return new Promise((resolve) => {
            void fetchResourceByTypeAndIds({
              ctwFetch,
              requestContext,
              graphqlClient,
              queryClient,
              telemetry,
              patient,
              resourceType: evidence.resourceType as never,
              resourceIds: [evidence.resourceId],
              fqsHotstorageRequestOptions,
            })
              .then((fetchedResources) => {
                resolve({ evidence, resource: fetchedResources[0] });
              })
              .catch(() => resolve({ evidence, resource: null }));
          });
        });

      Promise.allSettled(evidenceResourcePromises).then((results) => {
        setEvidenceItems(
          results
            .map((result) => (result.status === 'fulfilled' ? result.value : undefined))
            .filter((val: ClinicalEvidenceResource | undefined) => typeof val === 'object')
            .map((evidenceResource) =>
              createClinicalObjectGroupItem(evidenceResource.evidence, evidenceResource.resource),
            ),
        );
        setLoading(false);
      });
    }
  }, [
    requestContext,
    ctwFetch,
    graphqlClient,
    patient,
    telemetry,
    riskGap.id,
    documentReferencesQuery.data,
  ]);

  // This is subject to further work with UX in order to determine the correct dates to display in the card for related resources and evidence.
  // For now we're just going to show the recorded date for conditions and the dates for other resources will be displayed in their respective
  // details drawers or the document viewer.
  const displayDateForRiskGapRelatedResource = (
    resourceModel: FHIRModel<Resource>,
  ): string | undefined => {
    if (resourceModel.resourceType === 'Condition') {
      const recordedDate = (resourceModel.resource as Condition).recordedDate;
      return recordedDate ? `Recorded ${formatFHIRDate(recordedDate)}` : undefined;
    }

    return undefined;
  };

  const createClinicalObjectGroupItemForPayerRiskGapMeasureReport = (
    evidence: Evidence,
    resource: FHIRModel<Resource>,
  ): ClinicalObjectGroupItem => {
    return {
      id: resource.id,
      title: 'Payer CSV File',
      descriptors: undefined,
      telemetryContext: {
        riskGapEvidenceResourceId: resource.id,
        riskGapEvidenceResourceType: resource.resourceType,
      },
      content: <span className={tw`flex flex-col`}>{evidence.description}</span>,
      onClick: undefined,
    };
  };

  const createClinicalObjectGroupItemForDocRef = (
    evidence: Evidence,
    resource: FHIRModel<Resource>,
  ): ClinicalObjectGroupItem => {
    const doc = resource.resource as DocumentReference;
    const docContentType = doc.content?.find((c) => c.attachment?.contentType)?.attachment
      ?.contentType;

    let badgeLabel = 'Document';
    if (docContentType && IMAGE_RE.test(docContentType)) {
      badgeLabel = 'Image';
    }
    if (docContentType && PDF_RE.test(docContentType)) {
      badgeLabel = 'PDF';
    }

    const documentReferenceForEvidence = documentReferencesQuery.data?.find(
      (docRef) => docRef.id === resource.id,
    );

    const displayDate = displayDateForRiskGapRelatedResource(resource);

    return {
      id: resource.id,
      title: resource.title ?? evidence.description,
      descriptors: displayDate ? [{ content: displayDate }] : undefined,
      telemetryContext: {
        riskGapEvidenceResourceId: resource.id,
        riskGapEvidenceResourceType: resource.resourceType,
      },
      content: evidence.description === resource.title ? undefined : evidence.description,
      ...(documentReferenceForEvidence && {
        onClick: () => {
          if (patientContext) {
            openResourceModal({
              telemetry,
              model: documentReferenceForEvidence,
            });
          } else {
            window.open(
              pathWithParameters(
                `/patients/${patient.UPID}/overview`,
                {
                  resourceType: documentReferenceForEvidence.resourceType,
                  resourceId: documentReferenceForEvidence.id,
                  builderId: patient.builderId,
                },
                ['builderId'],
              ),
              '_blank',
            );
          }
        },
      }),
      ...(docContentType &&
        badgeLabel && {
          tags: [
            {
              variant: 'meta',
              label: badgeLabel,
            },
          ],
        }),
    };
  };

  const createClinicalObjectGroupItemForGenericFhirResource = (
    evidence: Evidence,
    resource: FHIRModel<Resource>,
  ): ClinicalObjectGroupItem => {
    let label: string;

    switch (resource.resourceType) {
      case 'DiagnosticReport': {
        const dr = resource.resource as DiagnosticReport;
        const categoryDisplay = dr?.category?.[0]?.text ?? dr?.category?.[0]?.coding?.[0]?.display;
        label = categoryDisplay ?? 'Diagnostic Report';
        break;
      }
      case 'MedicationStatement':
        label = 'Medication';
        break;
      case 'Condition':
        label = 'Condition';
        break;
      default:
        label =
          resource.resourceTypeTitle ??
          (resource.resourceType ? startCase(resource.resourceType) : undefined) ??
          'Evidence';
    }

    const displayDate = displayDateForRiskGapRelatedResource(resource);

    return {
      id: resource.id,
      title: resource.title ?? evidence.description,
      descriptors: displayDate ? [{ content: displayDate }] : undefined,
      telemetryContext: {
        riskGapEvidenceResourceId: resource.id,
        riskGapEvidenceResourceType: resource.resourceType,
      },
      content:
        evidence.description === resource.title || label === 'Condition' ? undefined : (
          <span className={tw`flex flex-col`}>{evidence.description}</span>
        ),
      onClick: () => {
        if (resource.resourceType === 'DocumentReference') {
          if (patientContext) {
            openResourceModal({
              telemetry,
              model: resource as DocumentModel,
            });
          } else {
            window.open(
              pathWithParameters(
                `/patients/${patient.UPID}/overview`,
                {
                  modalResourceType: resource.resourceType,
                  modalResourceId: resource.id,
                  builderId: patient.builderId,
                },
                ['builderId'],
              ),
              '_blank',
            );
          }
        } else if (patientContext) {
          pushView({
            patient,
            resourceType: resource.resourceType,
            resourceId: resource.id,
          });
        } else {
          window.open(
            pathWithParameters(
              `/patients/${patient.UPID}/overview`,
              {
                resourceType: resource.resourceType,
                resourceId: resource.id,
                builderId: patient.builderId,
              },
              ['builderId'],
            ),
            '_blank',
          );
        }
      },
    };
  };

  const createClinicalObjectGroupItem = (
    evidence: Evidence,
    resource: FHIRModel<Resource> | null,
  ): ClinicalObjectGroupItem => {
    if (!resource) {
      return {
        id: snakeCase(evidence.description),
        title: evidence.description,
        telemetryContext: {
          riskGapEvidenceResourceId: evidence.resourceId,
          riskGapEvidenceResourceType: evidence.resourceType,
        },
      };
    }

    if (resource.resourceType === 'DocumentReference') {
      return createClinicalObjectGroupItemForDocRef(evidence, resource);
    }
    if (
      resource.resourceType === 'MeasureReport' &&
      resource?.resource.meta?.profile?.includes(SYSTEM_URLS.PROFILE.PAYER_RISK_GAP_MEASURE_REPORT)
    ) {
      return createClinicalObjectGroupItemForPayerRiskGapMeasureReport(evidence, resource);
    }
    return createClinicalObjectGroupItemForGenericFhirResource(evidence, resource);
  };

  return (
    <ClinicalObjectGroup
      items={evidenceItems}
      loading={loading}
      focalHeader={true}
      label="Clinical Evidence"
    />
  );
};

interface MeasureReportRelatedRecordsProps {
  riskGap: RiskGapModel;
  patient: PatientModel;
}

const MeasureReportRelatedRecords = ({ riskGap, patient }: MeasureReportRelatedRecordsProps) => {
  const { pushView } = useResourceDrawer();
  const relatedRecordsQuery = useResources({
    identifiers: riskGap.relatedRecords,
    patient,
  });
  const { openResourceModal } = useResourceModal();
  const telemetry = useTelemetry();

  const patientContext = usePatientContextIfAvailable();

  return (
    <ClinicalObjectGroup
      label="Related Records"
      focalHeader={true}
      loading={relatedRecordsQuery.isLoading}
      items={compact(
        (relatedRecordsQuery.data ?? []).map((relatedRecord) => ({
          id: relatedRecord.id,
          label: relatedRecord.resourceTypeTitle,
          title: relatedRecord.title,
          onClick: () => {
            if (relatedRecord.resourceType === 'DocumentReference') {
              if (patientContext) {
                openResourceModal({
                  telemetry,
                  model: relatedRecord as DocumentModel,
                });
              } else {
                window.open(
                  pathWithParameters(
                    `/patients/${patient.UPID}/overview`,
                    {
                      resourceType: relatedRecord.resourceType,
                      resourceId: relatedRecord.id,
                      builderId: patient.builderId,
                    },
                    ['builderId'],
                  ),
                  '_blank',
                );
              }
            } else if (patientContext) {
              pushView({
                patient,
                resourceType: relatedRecord.resourceType,
                resourceId: relatedRecord.id,
              });
            } else {
              window.open(
                pathWithParameters(
                  `/patients/${patient.UPID}/overview`,
                  {
                    resourceType: relatedRecord.resourceType,
                    resourceId: relatedRecord.id,
                    builderId: patient.builderId,
                  },
                  ['builderId'],
                ),
                '_blank',
              );
            }
          },
        })),
      )}
    />
  );
};

// riskGapTypeToTitle converts the type of a risk gap to an overview title
function riskGapTypeToTitle(type: RiskGapType | NewGapType) {
  switch (type) {
    case 'recapture':
      return 'Recaptured Condition';
    case 'suspect':
      return 'Suspected Condition';
    case 'net-new':
      return 'Newly Diagnosed Condition';
    default:
      return 'Risk Gap Condition';
  }
}
